import type { Group, Object3D } from 'three';
import { Vector3 } from 'three';

import type { AnimationManager } from '../../../three-helpers/animation';
import type {
	SceneDiffManager,
	SceneManager,
	SceneTransitionManager
} from '../../managers';
import type { DataSource, ObjectMetadata, Transform } from '../../types';
import { VENT_CUTOUT_TYPE } from '../../managers/cutout-manager/helpers/vent-proxy.helpers';
import type { SceneChangeHints } from '../../../../controller/types/tool.types';

/**
 * How far into an appliance's dissolve (the transition manager's fadeOutDuration)
 * the island updates underneath it — the hole closes and neighbours re-space
 * while the mesh is still fading, instead of after it has gone.
 */
const HOLE_CLOSES_AT = 0.4;

export type SceneObjectDelegateConfig = {
	sceneManager: SceneManager | null;
	sceneTransitionManager: SceneTransitionManager | null;
	animationManager: AnimationManager | null;
	sceneDiffManager: SceneDiffManager | null;
	emit: (type: string, data: unknown) => void;
	rebuild: (sources?: Array<DataSource>) => Promise<void>;
	updateScene: (sources?: Array<DataSource>) => Promise<void>;
	getLastDataSources: () => Array<DataSource>;
	changeSurroundTexture: (key: string) => Promise<boolean>;
	changeCountertopTexture: (key: string) => void;
	changeFloorTexture: (key: string) => void;
	regenerateCutouts: () => Promise<void>;
	updateSceneSkipStructural: (sources: Array<DataSource>) => Promise<void>;
	/** Regenerate one island's countertop/surround holes from the domain, nothing else */
	regenerateIslandCutouts: (islandId: string) => Promise<void>;
	/** Build a single island by ID and swap it into the scene with animation */
	rebuildIslandGroup: (islandId: string) => Promise<Group | null>;
	/** Recalculate bounding box from current scene objects and smoothly reposition the camera */
	repositionCamera: () => void;
};

/**
 * Delegate that owns scene object CRUD operations, queries, and animations.
 * Extracted from ThreeSceneEngine to reduce file size.
 */
export class SceneObjectDelegate {
	#config: SceneObjectDelegateConfig;

	/** In-flight rebuild chain per island id — see rebuildIsland(). */
	#rebuildChains = new Map<string, Promise<void>>();

	constructor (config: SceneObjectDelegateConfig) {
		this.#config = config;
	}

	/**
	 * Add an object to the scene with optional metadata
	 */
	addObject (object: Object3D, metadata?: ObjectMetadata): void {
		if (metadata) {
			object.userData = { ...object.userData, ...metadata };
		}
		this.#config.sceneManager?.objectHolder?.add(object);
	}

	/**
	 * Remove an object from the scene by ID
	 */
	removeObject (id: string, animate = true): void {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return;

		let objectToRemove: Object3D | null = null;

		objectHolder.traverse((child) => {
			if (child.userData?.id === id) {
				objectToRemove = child;
			}
		});

		if (!objectToRemove) return;

		if (animate && this.#config.sceneTransitionManager) {
			this.#config.emit('object:removing', { id });
			const snapshot = {
				id,
				type: (objectToRemove as Object3D).userData?.type || 'unknown',
				object: objectToRemove,
				position: (objectToRemove as Object3D).position.clone(),
				rotation: {
					x: (objectToRemove as Object3D).rotation.x,
					y: (objectToRemove as Object3D).rotation.y,
					z: (objectToRemove as Object3D).rotation.z
				},
				hash: ''
			};
			this.#config.sceneTransitionManager.animateRemovals([snapshot]);
		} else {
			(objectToRemove as Object3D).parent?.remove(objectToRemove);
		}
	}

	/**
	 * Remove an object from the scene with dissolve animation
	 */
	async removeObjectAnimated (id: string): Promise<void> {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return;

		let objectToRemove: Object3D | null = null;

		objectHolder.traverse((child) => {
			if (child.userData?.id === id) {
				objectToRemove = child;
			}
		});

		if (!objectToRemove) return;

		if (this.#config.sceneTransitionManager) {
			this.#config.emit('object:removing', { id });
			const snapshot = {
				id,
				type: (objectToRemove as Object3D).userData?.type || 'unknown',
				object: objectToRemove,
				position: (objectToRemove as Object3D).position.clone(),
				rotation: {
					x: (objectToRemove as Object3D).rotation.x,
					y: (objectToRemove as Object3D).rotation.y,
					z: (objectToRemove as Object3D).rotation.z
				},
				hash: ''
			};
			await this.#config.sceneTransitionManager.animateRemovals([snapshot]);
		} else {
			(objectToRemove as Object3D).parent?.remove(objectToRemove);
		}
	}

	/**
	 * Apply targeted scene changes based on hint classification.
	 * Routes each hint type through the appropriate handler — per-island rebuild,
	 * animation, or texture swap. No full scene rebuild.
	 */
	async applyTargetedChanges (changes: SceneChangeHints): Promise<void> {
		console.log('[SceneObjectDelegate] applyTargetedChanges called', {
			islandResized: changes.islandResized?.length ?? 0,
			removed: changes.removed?.length ?? 0,
			added: changes.added?.length ?? 0,
			islandsAdded: changes.islandsAdded?.length ?? 0,
			islandsRemoved: changes.islandsRemoved?.length ?? 0,
			panelDeleted: changes.panelDeleted?.length ?? 0
		});

		const animations: Array<Promise<void>> = [];
		// Meshes dissolving out (removals and the outgoing half of a replacement)
		const dissolving: Array<{ object: Object3D; done: Promise<void> }> = [];
		const replacedIslands = new Set<string>();

		// Collect islands that need a per-island rebuild
		// Track resize-only islands separately so they get instant swap (no dissolve animation)
		const islandsToRebuild = new Set<string>();
		const resizeOnlyIslands = new Set<string>();

		// Structural changes — per-island rebuild (replaces old requiresRebuild path)
		if (changes.islandResized?.length) {
			console.log('[SceneObjectDelegate] islandResized detected', changes.islandResized);
			for (const { islandId } of changes.islandResized) {
				islandsToRebuild.add(islandId);
				resizeOnlyIslands.add(islandId);
			}
		}
		if (changes.panelDeleted?.length) {
			for (const { islandId } of changes.panelDeleted) {
				islandsToRebuild.add(islandId);
				resizeOnlyIslands.delete(islandId); // structural change — use animated transition
			}
		}

		// Appliance replacement — the old mesh dissolves (the delete transition), then the
		// island's structure is swapped in place exactly as an addition is: the new mesh fades
		// in, neighbours slide if the width changed, and the holes are regenerated. The island
		// itself never dissolves and fades back; its geometry is unchanged apart from the holes.
		// PanelItem.swapItem keeps the id, so the old mesh is removed before the swap, or the
		// swap would read it as a survivor to slide instead of a new mesh to fade in.
		if (changes.applianceReplaced?.length) {
			const objectHolder = this.#config.sceneManager?.objectHolder;
			for (const { oldId, islandId } of changes.applianceReplaced) {
				if (!islandId) continue;
				if (objectHolder && oldId) {
					const old = this.findObjectById(oldId);
					if (old?.userData?.type === 'appliance') dissolving.push(...this.#dissolveAppliance(oldId, objectHolder));
				}
				replacedIslands.add(islandId);
			}
		}

		// Material changes that require geometry rebuild (slat ↔ non-slat).
		// The await matters: the promise resolves true only for slat ↔ non-slat
		// transitions — plain texture changes apply the swap internally and
		// resolve false, skipping the rebuild path entirely. On the transition
		// path it resolves only after the new surround + corner materials are
		// loaded, which the island rebuilds below rely on. The await is bounded:
		// texture loads are stall-guarded and resolve null on failure instead
		// of rejecting or hanging.
		if (changes.surroundMaterialChanged) {
			const slatTransition = await this.#config.changeSurroundTexture(changes.surroundMaterialChanged.key);
			if (slatTransition) {
				// Slat ↔ non-slat transition changes geometry — rebuild all islands.
				// Use instant swap (resizeOnlyIslands) so the old island stays visible
				// until the new one is fully built, preventing a visible "shrink"
				// during the dissolve-out / fade-in gap.
				const objectHolder = this.#config.sceneManager?.objectHolder;
				if (objectHolder) {
					for (const child of objectHolder.children) {
						if (child.userData?.type === 'island' && child.userData?.id) {
							islandsToRebuild.add(child.userData.id);
							resizeOnlyIslands.add(child.userData.id);
							// Dim the island immediately so the user sees instant feedback
							// while the CSG rebuild runs in the background
							child.traverse((mesh: Object3D) => {
								if ((mesh as any).material) {
									const mat = (mesh as any).material;
									if (!mat.transparent) mat.transparent = true;
									mat.opacity = 0.5;
								}
							});
						}
					}
				}
			}
		}

		if (changes.countertopMaterialChanged) {
			this.#config.changeCountertopTexture(changes.countertopMaterialChanged.key);
		}

		// Direct object removals — skip items whose parent island is being rebuilt
		// (e.g. vent removal triggered by surround material change): the rebuild
		// replaces the entire group, so individual fade-outs would only add latency
		// and cause a visible "shrink" before it. An appliance is the exception: it
		// dissolves on its own (the delete transition) and its island — which every
		// panel_item.deleted also flags as resized — is swapped in place partway
		// through that fade (below). Only an island that is about to dissolve whole
		// takes its appliances with it.
		const islandsWithApplianceRemovals = new Set<string>();
		if (changes.removed?.length) {
			const objectHolder = this.#config.sceneManager?.objectHolder;

			for (const id of changes.removed) {
				const obj = objectHolder ? this.findObjectById(id) : null;
				const islandId = obj ? this.#islandIdOf(obj) : null;
				const islandRebuild = islandId !== null && islandsToRebuild.has(islandId)
					? (resizeOnlyIslands.has(islandId) ? 'swap' : 'dissolve')
					: null;
				if (obj?.userData?.type === 'appliance' && objectHolder) {
					if (islandRebuild === 'dissolve') continue;
					if (islandId !== null) islandsWithApplianceRemovals.add(islandId);
					dissolving.push(...this.#dissolveAppliance(id, objectHolder));
					continue;
				}
				if (islandRebuild !== null) continue;
				animations.push(this.removeObjectAnimated(id));
			}
		}

		// Island group removals (removes all children)
		if (changes.islandsRemoved?.length) {
			for (const islandId of changes.islandsRemoved) {
				// Don't rebuild an island we're about to remove
				islandsToRebuild.delete(islandId);
				animations.push(this.removeIslandGroupAnimated(islandId));
			}
		}

		// Position animations
		if (changes.moved?.length) {
			for (const move of changes.moved) {
				animations.push(this.animateObjectPosition(move.id, move.position));
			}
		}

		// Island group position animations
		if (changes.islandsMoved?.length) {
			for (const move of changes.islandsMoved) {
				// If island is being rebuilt, skip move — rebuild places it correctly
				if (!islandsToRebuild.has(move.id)) {
					animations.push(this.animateIslandPosition(move.id, move.position));
				}
			}
		}

		// Island rotation — animate rotation via registry
		if (changes.islandRotated?.length) {
			for (const { islandId } of changes.islandRotated) {
				// If island is being rebuilt, skip — rebuild places it correctly
				if (!islandsToRebuild.has(islandId)) {
					islandsToRebuild.add(islandId);
				}
			}
		}

		// Material updates (instant, no animation)
		if (changes.materials?.length) {
			for (const mat of changes.materials) {
				this.updateMaterialByType(mat.type, mat.key);
			}
		}

		// Appliance removals and replacements: partway through the dissolve the island
		// updates underneath the fading mesh — the hole closes, neighbours re-space and a
		// replacement fades in while the old one is still going. The fading meshes are
		// parked at the holder level first, under a different id, so the instant swap of
		// the island group neither takes them with it nor sweeps them as duplicates.
		// That update is the instant structural swap for an island flagged resized or
		// replaced-into, and whenever groups moved (every clean re-spaces); otherwise a
		// cutout pass alone, which reads the domain the mesh already left. The new group
		// builds from the start (a replacement's model may have to load) and swaps in at
		// the mid-fade mark, or as soon as it is ready after that.
		const updatedMidFade = new Set<string>();
		if (dissolving.length > 0 || replacedIslands.size > 0) {
			const structuralIslands = new Set([...islandsWithApplianceRemovals, ...replacedIslands]);
			const anyGroupMoved = Boolean(changes.groupsMoved?.length);
			const swaps = (islandId: string): boolean => anyGroupMoved || replacedIslands.has(islandId) || islandsToRebuild.has(islandId);
			const dissolveMs = this.#config.sceneTransitionManager?.config.fadeOutDuration ?? 400;
			for (const islandId of structuralIslands) updatedMidFade.add(islandId);
			const parked = (async (): Promise<void> => {
				await new Promise<void>((resolve) => setTimeout(resolve, dissolveMs * HOLE_CLOSES_AT));
				const objectHolder = this.#config.sceneManager?.objectHolder;
				for (const { object } of dissolving) {
					if (!objectHolder || !object.parent || object.parent === objectHolder) continue;
					object.userData = { ...object.userData, id: `${String(object.userData?.id)}-dissolving`, type: 'appliance-dissolving' };
					objectHolder.attach(object);
				}
			})();
			const structural = Promise.all([...structuralIslands].map((islandId) => (swaps(islandId)
				? this.rebuildIsland(islandId, false, { beforeSwap: () => parked })
				: parked.then(() => this.#config.regenerateIslandCutouts(islandId))))).then(() => undefined);
			animations.push(parked, structural, ...dissolving.map((d) => d.done));
		}

		// Wait for all direct animations
		await Promise.all(animations);

		// New islands added to the design — build + fade-in (no old island to remove)
		if (changes.islandsAdded?.length) {
			for (const islandId of changes.islandsAdded) {
				islandsToRebuild.add(islandId);
				// Not in resizeOnlyIslands — use animated fade-in
			}
		}

		// Additions — rebuild the island the appliance is being added to
		// Track added appliance IDs so rebuildIsland can fade in only the new ones
		if (changes.added?.length) {
			for (const obj of changes.added) {
				if (obj.islandId) {
					islandsToRebuild.add(obj.islandId);
					resizeOnlyIslands.add(obj.islandId); // instant swap, not full dissolve
				}
			}
		}

		// Group moves — diff + transition for repositioned appliances.
		// Skip if islands are already being rebuilt (additions handle redistribution).
		if (changes.groupsMoved?.length && islandsToRebuild.size === 0 && updatedMidFade.size === 0) {
			await this.#config.updateScene(this.#config.getLastDataSources());
			this.updateSceneSnapshot();
			return;
		}

		// Kick off each island rebuild with a setTimeout(0) yield between kickoffs so the
		// synchronous CSG work in each rebuild doesn't block the main thread continuously,
		// then await all in parallel so fade/slide animations overlap instead of stacking.
		if (islandsToRebuild.size > 0) {
			const rebuildPromises: Array<Promise<void>> = [];
			for (const id of islandsToRebuild) {
				// a mid-fade swap already read the final domain state for this island
				if (updatedMidFade.has(id)) continue;
				rebuildPromises.push(this.rebuildIsland(id, !resizeOnlyIslands.has(id)));
				await new Promise<void>(resolve => setTimeout(resolve, 0));
			}
			await Promise.all(rebuildPromises);
		}

		// All island positions updated — diff-based animation for repositioning.
		// Skip when islands were already rebuilt: the rebuild reads current domain
		// state (including updated positions), so running updateSceneSkipStructural
		// would trigger a full build that duplicates the just-rebuilt islands.
		if (changes.islandPositionsUpdated && islandsToRebuild.size === 0 && updatedMidFade.size === 0) {
			await this.#config.updateSceneSkipStructural(this.#config.getLastDataSources());
		}

		// When islands were added or removed, recalculate camera target and
		// bounding box, then smoothly slide the camera to frame the new layout.
		if (changes.islandsRemoved?.length || changes.islandsAdded?.length) {
			this.#config.repositionCamera();
		}

		// Update scene snapshot for future diffs
		this.updateSceneSnapshot();
	}

	/**
	 * Rebuild a single island: remove old group, build new group, add to scene.
	 * When `animate` is true (default for structural changes), uses dissolve + fade-in.
	 * When `animate` is false (resize), does an instant swap for a seamless visual update.
	 */
	async rebuildIsland (islandId: string, animate = true, options?: { beforeSwap?: () => Promise<void> }): Promise<void> {
		console.log('[SceneObjectDelegate] rebuildIsland called', { islandId, animate });

		// Serialize per island. A populate fires one rebuild per appliance ~20ms
		// apart while each build takes 300-900ms, so several ran against the same
		// island at once. Each captured the SAME old group before any of them
		// swapped, so every remove after the first hit an already-detached group
		// and its new group stacked on top of the live one. The duplicates share a
		// userData.id, so snapshotScene() records only one and the diff manager
		// never sees the ghost again — it survives every later diff and clears
		// only on a full rebuild (page refresh).
		const prior = this.#rebuildChains.get(islandId) ?? Promise.resolve();
		// A failed rebuild must not poison the island's queue.
		const run = prior.catch(() => null).then(() => this.#rebuildIslandNow(islandId, animate, options?.beforeSwap));
		this.#rebuildChains.set(islandId, run);

		try {
			await run;
		} finally {
			// Leave the entry alone when a newer rebuild has already chained behind us.
			if (this.#rebuildChains.get(islandId) === run) this.#rebuildChains.delete(islandId);
		}
	}

	/**
	 * Drop holder-level objects whose ids the incoming island group also carries.
	 * `oldIsland` is exempt — the caller removes that itself.
	 */
	#sweepOrphansFor (newIsland: Group, oldIsland: Object3D | null): void {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return;

		const rebuiltIds = new Set<string>();
		newIsland.traverse((child: Object3D) => {
			const id = child.userData?.id as string | undefined;
			if (id) rebuiltIds.add(id);
		});

		for (const child of [...objectHolder.children]) {
			const id = child.userData?.id as string | undefined;
			if (child === oldIsland || !id || !rebuiltIds.has(id)) continue;
			console.warn('[SceneObjectDelegate] rebuildIsland: dropping orphaned scene object', { id, type: child.userData?.type });
			objectHolder.remove(child);
		}
	}

	/** `beforeSwap` holds the built group back until it resolves (a dissolve's mid-fade mark). */
	async #rebuildIslandNow (islandId: string, animate: boolean, beforeSwap?: () => Promise<void>): Promise<void> {
		// Build new island group from current domain state
		const newIsland = await this.#config.rebuildIslandGroup(islandId);
		console.log('[SceneObjectDelegate] rebuildIsland newIsland built:', Boolean(newIsland), newIsland?.children?.length ?? 0, 'children');
		if (!newIsland) {
			console.warn(`[SceneObjectDelegate] rebuildIsland: builder returned null for ${islandId}`);
			return;
		}
		if (beforeSwap) await beforeSwap();

		// Resolve the group to replace AFTER the build, never before it. Reading it
		// up front is what let a concurrent rebuild swap the scene out from under
		// this one. The serialization above makes that unreachable; this keeps it
		// unreachable if a future caller ever bypasses the queue.
		const oldIsland = this.findIslandById(islandId);
		console.log('[SceneObjectDelegate] rebuildIsland oldIsland found:', Boolean(oldIsland));

		// Sweep loose copies of anything this rebuild is about to re-create.
		// Swapping the island group only takes away what is INSIDE it, so an object
		// sitting directly on the holder under an id the new group also carries
		// would survive the swap and render alongside its replacement. The diff
		// applier used to strand appliances there (fixed in `resolveAddedParent`);
		// this keeps the invariant true whatever a future caller does, because the
		// snapshot is keyed by id and cannot represent — or ever clean up — a
		// duplicate once one exists.
		this.#sweepOrphansFor(newIsland, oldIsland);

		if (animate) {
			// Dissolve old island with type-aware animation
			if (oldIsland && this.#config.sceneTransitionManager) {
				const snapshot = {
					id: islandId,
					type: 'island',
					object: oldIsland as Object3D,
					position: oldIsland.position.clone(),
					rotation: {
						x: oldIsland.rotation.x,
						y: oldIsland.rotation.y,
						z: oldIsland.rotation.z
					},
					hash: ''
				};
				await this.#config.sceneTransitionManager.animateRemovals([snapshot]);
			} else if (oldIsland) {
				oldIsland.parent?.remove(oldIsland);
			}

			// Add new island to scene
			this.#config.sceneManager?.objectHolder?.add(newIsland);

			// Animate addition via registry
			if (this.#config.sceneTransitionManager) {
				const sceneObject = {
					object: newIsland as Object3D,
					metadata: {
						id: islandId,
						type: 'island' as const,
						source: 'kitchen'
					}
				};
				await this.#config.sceneTransitionManager.animateAdditionByType(sceneObject);
			}
		} else {
			// Instant swap — slide existing appliances, fade in new ones
			// 1. Capture old appliance positions by ID
			const oldAppliancePositions = new Map<string, Vector3>();
			if (oldIsland) {
				oldIsland.traverse((child: Object3D) => {
					if (child.userData?.type === 'appliance' && child.userData?.id) {
						oldAppliancePositions.set(child.userData.id as string, child.position.clone());
					}
				});
				oldIsland.parent?.remove(oldIsland);
			}

			// 2. Add new island to scene
			this.#config.sceneManager?.objectHolder?.add(newIsland);

			// 3. Slide existing appliances, fade in new ones
			if (this.#config.animationManager) {
				const transitionAnimations: Array<Promise<void>> = [];
				newIsland.traverse((child: Object3D) => {
					if (child.userData?.type === 'appliance' && child.userData?.id) {
						const appId = child.userData.id as string;
						const oldPos = oldAppliancePositions.get(appId);
						if (oldPos) {
							// Existing appliance — slide from old position to new
							const targetPos = child.position.clone();
							child.position.copy(oldPos);
							transitionAnimations.push(
								this.#config.animationManager!.animatePositionAsync(
									`slide-${appId}`,
									oldPos,
									targetPos,
									300,
									(pos) => child.position.copy(pos)
								)
							);
						} else {
							// New appliance — fade in
							child.traverse((mesh: Object3D) => {
								if ((mesh as any).material) {
									const mat = (mesh as any).material;
									if (!mat.transparent) mat.transparent = true;
									const origOpacity = mat.opacity ?? 1;
									mat.opacity = 0;
									transitionAnimations.push(
										this.#config.animationManager!.animatePositionAsync(
											`fade-in-${appId}-${mesh.uuid}`,
											new Vector3(0, 0, 0),
											new Vector3(origOpacity, 0, 0),
											400,
											(v) => { mat.opacity = v.x; }
										)
									);
								}
							});
						}
					}
				});
				await Promise.all(transitionAnimations);
			}
		}
	}

	/** The id of the island group an object sits under, if any. */
	#islandIdOf (object: Object3D): string | null {
		let parent = object.parent;
		while (parent) {
			if (parent.userData?.type === 'island' && parent.userData?.id) return String(parent.userData.id);
			parent = parent.parent;
		}
		return null;
	}

	/** The appliance mesh, its linked appliance-cutout meshes and any `[id]-*` object, as removal snapshots. */
	#collectApplianceRemoval (applianceId: string, objectHolder: Object3D): Array<{ id: string; type: string; object: Object3D; position: Vector3; rotation: { x: number; y: number; z: number }; hash: string }> {
		const snapshots: Array<{ id: string; type: string; object: Object3D; position: Vector3; rotation: { x: number; y: number; z: number }; hash: string }> = [];
		const collected = new Set<Object3D>();
		const prefix = `${applianceId}-`;

		objectHolder.traverse((child) => {
			const childId = child.userData?.id as string | undefined;
			const childType = child.userData?.type as string | undefined;

			if (childId === applianceId) {
				this.#config.emit('object:removing', { id: applianceId, type: 'appliance' });
				collected.add(child);
				snapshots.push({ id: applianceId, type: childType || 'appliance', object: child, position: child.position.clone(), rotation: { x: child.rotation.x, y: child.rotation.y, z: child.rotation.z }, hash: '' });
				return;
			}
			// A vent's proxy is the same linked cutout mesh under its own type, and must leave with it.
			const isLinkedCutout = childType === 'appliance-cutout' || childType === VENT_CUTOUT_TYPE;
			if (isLinkedCutout && child.userData?.applianceId === applianceId && !collected.has(child)) {
				collected.add(child);
				snapshots.push({ id: childId || `cutout-${applianceId}`, type: childType, object: child, position: child.position.clone(), rotation: { x: child.rotation.x, y: child.rotation.y, z: child.rotation.z }, hash: '' });
				return;
			}
			if (childId && childId.startsWith(prefix) && !collected.has(child)) {
				collected.add(child);
				this.#config.emit('object:removing', { id: childId, type: childType || 'related' });
				snapshots.push({ id: childId, type: childType || 'related', object: child, position: child.position.clone(), rotation: { x: child.rotation.x, y: child.rotation.y, z: child.rotation.z }, hash: '' });
			}
		});

		return snapshots;
	}

	/**
	 * Start an appliance's dissolve (with its linked cutout meshes) and return each
	 * fading object with a promise for its end. The objects are removed from
	 * wherever they are when the transition finishes — the transition itself only
	 * removes an object still under its original parent, and the mid-fade
	 * structural update parks them at the holder level.
	 */
	#dissolveAppliance (applianceId: string, objectHolder: Object3D): Array<{ object: Object3D; done: Promise<void> }> {
		const snapshots = this.#collectApplianceRemoval(applianceId, objectHolder);
		if (snapshots.length === 0) return [];

		const transition = this.#config.sceneTransitionManager
			? this.#config.sceneTransitionManager.animateRemovals(snapshots)
			: Promise.resolve();
		const done = transition.then(() => {
			for (const snapshot of snapshots) snapshot.object.parent?.remove(snapshot.object);
		});

		return snapshots.map((snapshot) => ({ object: snapshot.object, done }));
	}

	/**
	 * Remove an island group and all its children with animation
	 */
	async removeIslandGroupAnimated (islandId: string): Promise<void> {
		const island = this.findIslandById(islandId);
		if (!island) return;

		this.#config.emit('object:removing', { id: islandId, type: 'island' });

		if (this.#config.sceneTransitionManager) {
			const snapshot = {
				id: islandId,
				type: 'island',
				object: island,
				position: island.position.clone(),
				rotation: {
					x: island.rotation.x,
					y: island.rotation.y,
					z: island.rotation.z
				},
				hash: ''
			};
			await this.#config.sceneTransitionManager.animateRemovals([snapshot]);
		} else {
			island.parent?.remove(island);
		}
	}

	/**
	 * Find an island by ID
	 */
	findIslandById (islandId: string): Group | null {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return null;

		for (const child of objectHolder.children) {
			if (child.userData?.type === 'island' && child.userData?.id === islandId) {
				return child as Group;
			}
		}
		return null;
	}

	/**
	 * Find an object by ID in the scene
	 */
	findObjectById (objectId: string): Object3D | null {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return null;

		let foundObject: Object3D | null = null;
		objectHolder.traverse((child: Object3D) => {
			if (child.userData?.id === objectId) {
				foundObject = child;
			}
		});

		return foundObject;
	}

	/**
	 * Animate an object to a new position
	 */
	async animateObjectPosition (
		id: string,
		newPosition: { x: number; y: number; z: number }
	): Promise<void> {
		const obj = this.findObjectById(id);
		if (!obj || !this.#config.animationManager) return;

		const targetPos = new Vector3(newPosition.x, newPosition.y, newPosition.z);

		await this.#config.animationManager.animatePositionAsync(
			`move-${id}`,
			obj.position.clone(),
			targetPos,
			300,
			(pos) => obj.position.copy(pos)
		);
	}

	/**
	 * Animate an island group to a new position (moves all children together)
	 */
	async animateIslandPosition (
		islandId: string,
		newPosition: { x: number; y: number; z: number }
	): Promise<void> {
		const island = this.findIslandById(islandId);
		if (!island || !this.#config.animationManager) return;

		const targetPos = new Vector3(newPosition.x, newPosition.y, newPosition.z);

		await this.#config.animationManager.animatePositionAsync(
			`move-island-${islandId}`,
			island.position.clone(),
			targetPos,
			300,
			(pos) => island.position.copy(pos)
		);
	}

	/**
	 * Add objects to the scene via per-island rebuild.
	 * Groups additions by island and rebuilds each affected island once.
	 */
	async addObjectsToScene (objects: Array<{ id: string; islandId?: string; sku?: string }>): Promise<void> {
		if (objects.length === 0) return;

		const islandsToRebuild = new Set<string>();
		for (const obj of objects) {
			this.#config.emit('object:added', { id: obj.id, type: 'appliance', pending: true });
			if (obj.islandId) {
				islandsToRebuild.add(obj.islandId);
			}
		}

		if (islandsToRebuild.size > 0) {
			await Promise.all(
				[...islandsToRebuild].map(id => this.rebuildIsland(id))
			);
		}
	}

	/**
	 * Update material by type (countertop, surround, etc.)
	 */
	updateMaterialByType (materialType: string, key: string): void {
		switch (materialType) {
			case 'countertop':
				this.#config.changeCountertopTexture(key);
				break;
			case 'surround':
				this.#config.changeSurroundTexture(key);
				break;
			case 'floor':
				this.#config.changeFloorTexture(key);
				break;
			default:
				console.warn(`Unknown material type: ${materialType}`);
		}
	}

	/**
	 * Update scene snapshot for future diff calculations
	 */
	updateSceneSnapshot (): void {
		if (this.#config.sceneDiffManager && this.#config.sceneManager?.objectHolder) {
			this.#config.sceneDiffManager.snapshotScene(this.#config.sceneManager.objectHolder);
		}
	}

	/**
	 * Update object transform
	 */
	updateObject (id: string, transform: Transform): void {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return;

		objectHolder.traverse((child) => {
			if (child.userData?.id === id) {
				if (transform.position) {
					child.position.set(
						transform.position.x,
						transform.position.y,
						transform.position.z
					);
				}
				if (transform.rotation) {
					child.rotation.set(
						transform.rotation.x,
						transform.rotation.y,
						transform.rotation.z
					);
				}
				if (transform.scale) {
					child.scale.set(
						transform.scale.x,
						transform.scale.y,
						transform.scale.z
					);
				}
			}
		});
	}

	/**
	 * Get an object by ID
	 */
	getObject (id: string): Object3D | null {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return null;

		let found: Object3D | null = null;
		objectHolder.traverse((child) => {
			if (child.userData?.id === id) {
				found = child;
			}
		});
		return found;
	}

	/**
	 * Clear all objects from scene
	 */
	clearObjects (): void {
		const objectHolder = this.#config.sceneManager?.objectHolder;
		if (!objectHolder) return;

		while (objectHolder.children.length > 0) {
			objectHolder.remove(objectHolder.children[0]);
		}
	}

}
